<script setup>
/* Imports */
import {
  computed,
  inject,
  ref,
} from 'vue';
import { useScreenWidth } from '../composables/useScreenWidth.js';

/* Initialize composables */
const { isTablet } = useScreenWidth();

/* Inject */
const locale = inject('locale') || ref('en-US');
const translations = inject('translations');
const subdirectory = inject('subdirectory') || ref('marine');

/* Marine Maps URL */
const marineMapsUrl = (mapType) => {
  const {
    pathname = `/${locale.value}/${subdirectory.value}/`,
    search: existingSearchParams,
  } = window?.location || {};
  const searchParams = new URLSearchParams(existingSearchParams);

  if (searchParams.has('maps')) {
    searchParams.delete('maps');
  }

  if (searchParams.has('overlay')) {
    searchParams.delete('overlay');
  }

  searchParams.set('maps', mapType);
  searchParams.set('overlay', 'false');

  return `${pathname}?${searchParams.toString()}`;
};

/* Toggle Map Brand -> Update URL with selected map brand */
const toggleMapBrand = (mapType) => {
  window.location.href = marineMapsUrl(mapType);
};

/* Computed - Card Width */
const cardWidth = computed(() => (isTablet.value ? 'half' : 'fourth'));

/* Computed - Cards Data */
const cards = computed(() => [
  {
    id: 'garmin',
    href: '',
    width: cardWidth.value,
    copy: {
      content: translations.value?.ITFE_MARINE_MAPS_LEFT_CARD_CONTENT,
      textAlign: 'left',
    },
    heading: {
      content: translations.value?.ITFE_MARINE_MAPS_LEFT_CARD_HEADING,
      size: '2',
      tag: '2',
      styleObject: {
        textTransform: 'uppercase',
        textAlign: 'left',
      },
    },
    ctas: [
      {
        text: translations.value?.ITFE_MARINE_MAPS_OVERLAY_BUTTON,
        theme: 'candy-blue',
        size: 'large',
        href: marineMapsUrl('garmin'),
      },
    ],
  },
  {
    id: 'another-brand',
    width: cardWidth.value,
    copy: {
      content: translations.value?.ITFE_MARINE_MAPS_RIGHT_CARD_CONTENT,
      textAlign: 'left',
    },
    heading: {
      content: translations.value?.ITFE_MARINE_MAPS_RIGHT_CARD_HEADING,
      size: '2',
      tag: '2',
      styleObject: {
        textTransform: 'uppercase',
        textAlign: 'left',
      },
    },
    ctas: [
      {
        text: translations.value?.ITFE_MARINE_MAPS_OVERLAY_BUTTON,
        theme: 'candy-blue',
        size: 'large',
        href: marineMapsUrl('another-brand'),
      },
    ],
  },
]);

/* Computed - Background Image */
const computedBackgroundImage = computed(() => `url(${translations.value.ITFE_MARINE_MAPS_OVERLAY_BACKGROUND_IMAGE || ''})`);
</script>

<template>
  <div
    class="interstitial"
    data-testid="interstitial"
  >
    <g-heading
      class="interstitial__heading"
      size="1"
      text-align="center"
      case-transform="upper"
    >
      <h1 v-html="translations.ITFE_MARINE_MAPS_OVERLAY_HEADING" />
    </g-heading>

    <div class="interstitial__card-container">
      <g-card
        v-for="mapCard in cards"
        :key="mapCard.id"
        :card="mapCard"
        class="interstitial__card-container__card"
        data-testid="interstitial-card"
        @click="toggleMapBrand(mapCard.id)"
      >
        <g-heading
          v-if="mapCard.heading"
          slot="heading"
          :text-align="mapCard.heading.styleObject.textAlign"
          :size="mapCard.heading.size"
          case-transform="upper"
          class="interstitial__card-container__card__heading"
        >
          <!-- eslint-disable vue/no-v-text-v-html-on-component -->
          <component
            :is="'h' + mapCard.heading.tag"
            v-html="mapCard.heading.content"
          />
          <!-- eslint-enable vue/no-v-text-v-html-on-component -->
        </g-heading>
        <!-- eslint-disable vue/no-v-text-v-html-on-component -->
        <g-copy
          v-if="mapCard.copy"
          slot="copy"
          :text-align="mapCard.copy.textAlign"
          v-html="mapCard.copy.content"
        />
        <!-- eslint-enable vue/no-v-text-v-html-on-component -->
      </g-card>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.interstitial {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  /* stylelint-disable-next-line value-keyword-case */
  background-image: v-bind(computedBackgroundImage);
  background-repeat: no-repeat;
  background-size: cover;

  &__heading {
    margin-top: 2rem;
  }

  &__card-container {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    width: 100%;
    padding: 0 0.25rem 2.5rem;
    margin-top: 1rem;
    gap: 2rem;

    @include breakpoint('sm') {
      padding: 0 2rem;
    }

    @include breakpoint('lg') {
      padding: 0;
    }

    :deep(.g__card__content) {
      padding: 0.75rem;
    }

    :deep(.g__button) {
      margin-bottom: 1.5rem;
    }

    :deep(.g__heading h2) {
      margin: 0;
    }
  }
}
</style>
